import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import AddToTripDropdown from './AddToTripDropdown'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { isPlaceInItinerary, getItineraryDayLabel, findPlaceStopInItinerary, findOfferStopDetails, findNearestStop, type Coordinates } from 'tripPlanner/utils/chatItinerary'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'

interface Props {
  itineraryData: TripItineraryData
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  placeName: string
  placeId?: string
  offerId?: string
  offerType?: string
  coordinates?: Coordinates
  variant?: 'filled' | 'outline'
  onAfterAction?: () => void
}

function AddToTripAction({
  itineraryData,
  onSendMessage,
  placeName,
  placeId,
  offerId,
  offerType,
  coordinates,
  variant = 'outline',
  onAfterAction,
}: Props) {
  const intl = useIntl()
  const addToTripLabel = intl.formatMessage({
    id: 'WXmmZc1aN3gIpc4CZ7k4',
    defaultMessage: 'Add to trip',
    description: 'Button to add a place to the trip itinerary in the trip planner',
  })

  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const offerStopDetails = useMemo(
    () => findOfferStopDetails(offerId, itineraryData),
    [offerId, itineraryData],
  )

  const placeInItinerary = useMemo(
    () => !!offerStopDetails || isPlaceInItinerary(placeId, itineraryData),
    [offerStopDetails, placeId, itineraryData],
  )

  const itineraryDayLabel = useMemo(() => {
    if (offerStopDetails) {
      return offerStopDetails.dayNumber !== undefined
        ? getLocalisedDayLabel(intl, offerStopDetails.dayNumber, offerStopDetails.dayDate)
        : intl.formatMessage({
            id: 'MgK5ISSCJKWklR8eg12e',
            defaultMessage: 'To be planned',
            description: 'Label for unassigned trip itinerary items that have no scheduled day',
          })
    }
    return getItineraryDayLabel(placeId, itineraryData)
  }, [offerStopDetails, placeId, itineraryData, intl])

  const placeStopInfo = useMemo(
    () => findPlaceStopInItinerary(placeId, itineraryData),
    [placeId, itineraryData],
  )

  const nearestStop = useMemo(
    () => findNearestStop(coordinates, itineraryData?.stops ?? []),
    [coordinates, itineraryData?.stops],
  )

  const initialStop = offerStopDetails?.stop ?? placeStopInfo?.stop ?? nearestStop

  const messageOptions = useMemo<SendMessageOptions | undefined>(
    () => offerId ? { offerId, offerType } : undefined,
    [offerId, offerType],
  )

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <>
      <AssistChip
        variant={variant}
        size="medium"
        ref={triggerRef}
        onClick={toggleDropdown}
        startIcon={placeInItinerary ? undefined : <LinePlusIcon />}
        endIcon={placeInItinerary ? <LineAngleDownIcon /> : undefined}
      >
        {placeInItinerary ? itineraryDayLabel : addToTripLabel}
      </AssistChip>
      <AddToTripDropdown
        open={isOpen}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        itineraryData={itineraryData}
        placeName={placeName}
        onSendMessage={onSendMessage}
        sendMessageOptions={messageOptions}
        onAfterAction={onAfterAction}
        initialStop={initialStop}
        currentDayNumber={offerStopDetails?.dayNumber ?? placeStopInfo?.dayNumber}
      />
    </>
  )
}

export default AddToTripAction
